/**
 * @fileoverview Settings Page
 * @theme Polished Corporate - Teal accents, refined shadows
 */

import { useState } from 'react';
import { Button } from '@components/ui/button';

interface SettingSection {
  id: string;
  title: string;
  description: string;
  icon: JSX.Element;
}

const settingSections: SettingSection[] = [
  {
    id: 'general',
    title: 'General',
    description: 'Basic application settings',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    id: 'notifications',
    title: 'Notifications',
    description: 'Email and push notification preferences',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
  },
  {
    id: 'billing',
    title: 'Billing',
    description: 'Subscription and payment settings',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
  },
  {
    id: 'integrations',
    title: 'Integrations',
    description: 'Connected apps and services',
    icon: (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
      </svg>
    ),
  },
];

export function SettingsPage(): JSX.Element {
  const [activeSection, setActiveSection] = useState('general');
  const [settings, setSettings] = useState({
    currency: 'USD',
    dateFormat: 'MM/DD/YYYY',
    language: 'en',
    darkMode: false,
    emailNotifications: true,
    pushNotifications: true,
    weeklyReport: true,
    overdueAlerts: true,
    paymentReminders: true,
  });

  const handleToggle = (key: keyof typeof settings): void => {
    setSettings({ ...settings, [key]: !settings[key] });
  };

  return (
    <div className="min-h-screen bg-gray-50/30">
      <div className="container py-6">
        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-64 shrink-0">
            <div className="rounded-xl bg-white p-3 shadow-card">
              <nav className="space-y-1">
                {settingSections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all ${
                      activeSection === section.id
                        ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white'
                        : 'text-gray-600 hover:bg-teal-50 hover:text-teal-700'
                    }`}
                  >
                    {section.icon}
                    <span className="text-sm font-medium">{section.title}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="rounded-xl bg-white p-6 shadow-card">
              {activeSection === 'general' && (
                <>
                  <h3 className="font-semibold text-gray-900">General Settings</h3>
                  <p className="mt-1 text-sm text-gray-500">Configure basic application preferences</p>

                  <div className="mt-6 space-y-6">
                    <div className="grid gap-6 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Currency</label>
                        <select
                          value={settings.currency}
                          onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                          className="mt-1.5 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-2.5 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                        >
                          <option value="USD">USD - US Dollar</option>
                          <option value="EUR">EUR - Euro</option>
                          <option value="GBP">GBP - British Pound</option>
                          <option value="BRL">BRL - Brazilian Real</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Date Format</label>
                        <select
                          value={settings.dateFormat}
                          onChange={(e) => setSettings({ ...settings, dateFormat: e.target.value })}
                          className="mt-1.5 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-2.5 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                        >
                          <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                          <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                          <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Language</label>
                        <select
                          value={settings.language}
                          onChange={(e) => setSettings({ ...settings, language: e.target.value })}
                          className="mt-1.5 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-2.5 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                        >
                          <option value="en">English</option>
                          <option value="es">Spanish</option>
                          <option value="pt">Portuguese</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex items-center justify-between rounded-xl border border-gray-100 p-4">
                      <div>
                        <p className="font-medium text-gray-900">Dark Mode</p>
                        <p className="text-sm text-gray-500">Use dark theme for the interface</p>
                      </div>
                      <button
                        onClick={() => handleToggle('darkMode')}
                        className={`relative h-6 w-11 rounded-full transition-colors ${
                          settings.darkMode ? 'bg-teal-500' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                            settings.darkMode ? 'left-5' : 'left-0.5'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </>
              )}

              {activeSection === 'notifications' && (
                <>
                  <h3 className="font-semibold text-gray-900">Notification Preferences</h3>
                  <p className="mt-1 text-sm text-gray-500">Choose how you want to be notified</p>

                  <div className="mt-6 space-y-4">
                    {[
                      { key: 'emailNotifications', title: 'Email Notifications', desc: 'Receive notifications via email' },
                      { key: 'pushNotifications', title: 'Push Notifications', desc: 'Receive browser push notifications' },
                      { key: 'weeklyReport', title: 'Weekly Report', desc: 'Get a summary of your finances every week' },
                      { key: 'overdueAlerts', title: 'Overdue Alerts', desc: 'Get notified when bills or invoices are overdue' },
                      { key: 'paymentReminders', title: 'Payment Reminders', desc: 'Remind me before payment due dates' },
                    ].map((item) => (
                      <div key={item.key} className="flex items-center justify-between rounded-xl border border-gray-100 p-4">
                        <div>
                          <p className="font-medium text-gray-900">{item.title}</p>
                          <p className="text-sm text-gray-500">{item.desc}</p>
                        </div>
                        <button
                          onClick={() => handleToggle(item.key as keyof typeof settings)}
                          className={`relative h-6 w-11 rounded-full transition-colors ${
                            settings[item.key as keyof typeof settings] ? 'bg-teal-500' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                              settings[item.key as keyof typeof settings] ? 'left-5' : 'left-0.5'
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {activeSection === 'billing' && (
                <>
                  <h3 className="font-semibold text-gray-900">Billing & Subscription</h3>
                  <p className="mt-1 text-sm text-gray-500">Manage your subscription and payment methods</p>

                  <div className="mt-6">
                    <div className="rounded-xl border border-teal-200 bg-teal-50/50 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="inline-flex rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-medium text-teal-700">
                            Professional Plan
                          </span>
                          <p className="mt-2 text-2xl font-bold text-gray-900">$49<span className="text-sm font-normal text-gray-500">/month</span></p>
                          <p className="text-sm text-gray-500">Next billing date: Feb 19, 2026</p>
                        </div>
                        <Button variant="outline" className="rounded-xl border-teal-200 text-teal-600 hover:bg-teal-100">
                          Upgrade Plan
                        </Button>
                      </div>
                    </div>

                    <div className="mt-6">
                      <h4 className="font-medium text-gray-900">Payment Method</h4>
                      <div className="mt-3 flex items-center justify-between rounded-xl border border-gray-100 p-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-14 items-center justify-center rounded-lg bg-gray-100">
                            <span className="text-xs font-bold text-gray-600">VISA</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">**** **** **** 4242</p>
                            <p className="text-sm text-gray-500">Expires 12/2027</p>
                          </div>
                        </div>
                        <Button variant="outline" className="rounded-xl border-gray-200">
                          Update
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {activeSection === 'integrations' && (
                <>
                  <h3 className="font-semibold text-gray-900">Integrations</h3>
                  <p className="mt-1 text-sm text-gray-500">Connect your favorite apps and services</p>

                  <div className="mt-6 space-y-4">
                    {[
                      { name: 'QuickBooks', desc: 'Sync accounting data', connected: true },
                      { name: 'Stripe', desc: 'Payment processing', connected: true },
                      { name: 'Slack', desc: 'Team notifications', connected: false },
                      { name: 'Google Drive', desc: 'Document storage', connected: false },
                    ].map((integration) => (
                      <div key={integration.name} className="flex items-center justify-between rounded-xl border border-gray-100 p-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100">
                            <span className="text-sm font-bold text-gray-600">{integration.name.charAt(0)}</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{integration.name}</p>
                            <p className="text-sm text-gray-500">{integration.desc}</p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          className={`rounded-xl ${
                            integration.connected
                              ? 'border-emerald-200 text-emerald-600 hover:bg-emerald-50'
                              : 'border-gray-200'
                          }`}
                        >
                          {integration.connected ? 'Connected' : 'Connect'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="mt-6 flex justify-end border-t border-gray-100 pt-6">
                <Button className="rounded-xl bg-gradient-to-r from-teal-500 to-teal-600 text-white">
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
